import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import React, { useState } from "react";
import { useFragment } from "react-relay";

import { AnimateHeight } from "@whoosh/design-system/src/components/controls/Accordion/AnimateHeight";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { PackageBenefitsSummary_package$key } from "./__generated__/PackageBenefitsSummary_package.graphql";

type Props = {
  packageRef: PackageBenefitsSummary_package$key | null | undefined;
  hideTitle?: boolean;
  maxInitialDisplay?: number;
};

export const PackageBenefitsSummary = ({
  packageRef,
  hideTitle = false,
  maxInitialDisplay,
}: Props) => {
  const [showExpanded, setShowExpanded] = useState(false);

  const pkg = useFragment(PackageBenefitsSummaryFragment, packageRef);

  const benefits =
    pkg?.benefitsSummary?.flatMap((benefit) =>
      benefit?.description ? [benefit.description] : []
    ) || [];

  const initialShownBenefits = maxInitialDisplay
    ? benefits.slice(0, maxInitialDisplay)
    : benefits;
  const remainingBenefits = maxInitialDisplay
    ? benefits.slice(maxInitialDisplay)
    : [];

  return benefits.length > 0 ? (
    <VStack gap={3}>
      {!hideTitle ? (
        <Text
          variant="mainTiny"
          color="text-secondary"
          textTransform="uppercase"
        >{t`Benefits`}</Text>
      ) : null}
      <VStack gap={3}>
        {initialShownBenefits.map((benefit, i) => (
          <HStack key={i} gap={2}>
            <Box height={16} width={16} alignItems="center">
              <Text variant="mainTiny">✦</Text>
            </Box>
            <Text>{benefit}</Text>
          </HStack>
        ))}
        {showExpanded ? (
          <AnimateHeight enterFrom="bottom" hide={!showExpanded}>
            {remainingBenefits.map((benefit, i) => (
              <HStack key={`extra-${i}`} gap={2}>
                <Box height={16} width={16} alignItems="center">
                  <Text variant="mainTiny">✦</Text>
                </Box>
                <Text>{benefit}</Text>
              </HStack>
            ))}
          </AnimateHeight>
        ) : null}
        {remainingBenefits.length > 0 ? (
          <Text
            key="toggle-extra-benefits"
            color="text-success"
            onPress={() => setShowExpanded(!showExpanded)}
            textDecorationLine="underline"
          >
            {showExpanded
              ? t`Hide`
              : t`See ${remainingBenefits.length} more benefit${
                  remainingBenefits.length > 1 ? "s" : ""
                }`}
          </Text>
        ) : null}
      </VStack>
    </VStack>
  ) : null;
};

const PackageBenefitsSummaryFragment = graphql`
  fragment PackageBenefitsSummary_package on Package {
    benefitsSummary {
      description
    }
  }
`;
